/**
 * Dates, times and sizes (PROJECT-KNOWLEDGE.md §Formatting & copy; the rules are `AGENTS.md`
 * "Formatting and dates", i.e. `design-docs/site-time-zones.md` §8.3).
 *
 * A DAY (`shipDay`, `salesDate`, `closedOn`) is the calendar date at the site its column
 * describes, and renders with NO timezone shift: `new Date('2026-09-16')` parses as UTC midnight
 * and shifts a day backwards west of Greenwich, so a day string is split and rendered from its
 * own parts. A TIMESTAMP renders in the viewer's (browser's) timezone, 24-hour.
 *
 * "TODAY" IS NOT ONE THING. There are three, and a call site chooses (§8.3 spots 3-9):
 * `todayIso`/`viewerToday` (the viewer's own day), `siteToday` (a site's day, in that site's zone)
 * and `planningDay` (the current plan's first day, for a register that spans sites).
 */
import type { PlanningRun, components } from "../api/client";
import type { Labels } from "./labels";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const pad2 = (n: number) => String(n).padStart(2, "0");

/** `'2026-09-16'` → `'16 Sep 2026'`. No Date construction, so no timezone shift. */
export function formatDate(day: string | null | undefined): string {
  if (!day) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(day);
  if (!match) return day;
  const [, year, month, date] = match as unknown as [string, string, string, string];
  const monthName = MONTHS[Number(month) - 1];
  if (!monthName) return day;
  return `${Number(date)} ${monthName} ${year}`;
}

/**
 * `'2026-09-16'` plus `n` days, as a day string. Pure civil arithmetic: the date is built in UTC
 * from its own parts and read back in UTC, so no zone ever shifts it (`AGENTS.md` rule 1). Returns
 * the input unchanged when it is not a `YYYY-MM-DD` day.
 */
export function addDaysIso(day: string, n: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return day;
  const [, year, month, date] = match as unknown as [string, string, string, string];
  const shifted = new Date(Date.UTC(Number(year), Number(month) - 1, Number(date) + n));
  return `${shifted.getUTCFullYear()}-${pad2(shifted.getUTCMonth() + 1)}-${pad2(shifted.getUTCDate())}`;
}

/**
 * The VIEWER's day: the browser's LOCAL day as `YYYY-MM-DD` — built from its parts, so never
 * shifted by UTC. Right for a filter over feed receipts, runs or audit entries (instants with no
 * site). It is NOT a site's today and NOT the planning day: use `siteToday` or `planningDay`.
 */
export function todayIso(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

/** Whose day a "today" default is. Chosen per use, never assumed (`AGENTS.md`). */
export type DayScope = "viewer" | "site" | "planning";

/** A default day, with the reason it is that day: the register's chip and tooltip say it. */
export interface DayDefault {
  /** `YYYY-MM-DD`. */
  day: string;
  scope: DayScope;
  /** The IANA zone the day was read in: set only for `scope: "site"`. */
  zone: string | null;
}

/** The viewer's own day as a `DayDefault` (`todayIso`, with its scope). */
export function viewerToday(now: Date = new Date()): DayDefault {
  return { day: todayIso(now), scope: "viewer", zone: null };
}

/**
 * The civil day at `now` in the IANA zone `timeZone`, as `YYYY-MM-DD`, or `null` when this
 * browser does not know the zone. The one place an instant becomes a day in a zone that is not
 * the viewer's: it reads the zone's own wall clock, so the result does not depend on the zone the
 * process runs in.
 */
export function todayInZone(timeZone: string, now: Date = new Date()): string | null {
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now);
  } catch {
    /* RangeError: not a zone this browser knows. */
    return null;
  }
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((candidate) => candidate.type === type)?.value;
  const year = part("year");
  const month = part("month");
  const day = part("day");
  return year && month && day ? `${year}-${month}-${day}` : null;
}

/**
 * A SITE's day: today at the site, read in its own zone, or in the tenant's default zone when the
 * site has none (`Site.timeZone ?? default`). With neither, or a zone this browser cannot read, it
 * falls back to the viewer's day and says so (`scope: "viewer"`), so the caller can word it
 * honestly rather than call the viewer's day the site's.
 */
export function siteToday(
  zones: { siteZone?: string | null | undefined; defaultZone?: string | null | undefined },
  now: Date = new Date(),
): DayDefault {
  const zone = zones.siteZone || zones.defaultZone || null;
  const day = zone ? todayInZone(zone, now) : null;
  return day && zone ? { day, scope: "site", zone } : viewerToday(now);
}

/**
 * The PLANNING day: the current plan's first day, the default for a register that spans sites
 * (it has no one site to ask). With no plan yet it falls back to the viewer's day and says so.
 */
export function planningDay(
  planningStartDay: string | null | undefined,
  now: Date = new Date(),
): DayDefault {
  return planningStartDay
    ? { day: planningStartDay, scope: "planning", zone: null }
    : viewerToday(now);
}

/** A filter chip's word for a default day: "From plan start" is never called "From today". */
export function fromDayLabel(scope: DayScope): string {
  return scope === "planning" ? "From plan start" : "From today";
}

/** The tooltip that says whose day a default is, and in which zone. */
export function dayDefaultTitle(value: DayDefault): string {
  if (value.scope === "site") return `Today at the site (${value.zone ?? "its zone"})`;
  if (value.scope === "planning") return "The current plan's first day";
  return "Today in your time zone";
}

/** `'2026-09-16T09:14:00Z'` → `'16 Sep 2026, 09:14'` in the browser's timezone, 24-hour. */
export function formatDateTime(isoUtc: string | null | undefined): string {
  if (!isoUtc) return "—";
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return isoUtc;
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${pad2(d.getHours())}:${pad2(
    d.getMinutes(),
  )}`;
}

/** Time only, 24-hour, browser timezone: `'09:14'`. */
export function formatTime(isoUtc: string | null | undefined): string {
  if (!isoUtc) return "—";
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return isoUtc;
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

/**
 * Relative under 7 days (`'2 h ago'`), absolute beyond. `title` is always the absolute value, for
 * the hover.
 */
export function formatRelative(
  isoUtc: string | null | undefined,
  now: Date = new Date(),
): { text: string; title: string } {
  if (!isoUtc) return { text: "—", title: "" };
  const d = new Date(isoUtc);
  if (Number.isNaN(d.getTime())) return { text: isoUtc, title: isoUtc };

  const title = formatDateTime(isoUtc);
  const delta = now.getTime() - d.getTime();
  const abs = Math.abs(delta);
  if (abs >= WEEK) return { text: title, title };

  const suffix = (value: string) => (delta >= 0 ? `${value} ago` : `in ${value}`);
  if (abs < MINUTE) return { text: delta >= 0 ? "just now" : "in a moment", title };
  if (abs < HOUR) return { text: suffix(`${Math.floor(abs / MINUTE)} min`), title };
  if (abs < DAY) return { text: suffix(`${Math.floor(abs / HOUR)} h`), title };
  return { text: suffix(`${Math.floor(abs / DAY)} d`), title };
}

/** Elapsed time: `m:ss` under an hour, `h:mm` beyond. Never a percentage. */
export function formatElapsed(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  if (total < HOUR / 1000) return `${Math.floor(total / 60)}:${pad2(total % 60)}`;
  return `${Math.floor(total / 3600)}:${pad2(Math.floor((total % 3600) / 60))}`;
}

/** B / kB / MB, one decimal above bytes. */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1000) return `${Math.round(bytes)} B`;
  if (bytes < 1_000_000) return `${(bytes / 1000).toFixed(1)} kB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/** `1 day`, `2 days`. */
export function formatDays(count: number): string {
  return `${count} ${count === 1 ? "day" : "days"}`;
}

type PlanningRule = Pick<
  PlanningRun,
  "planningAnchorTimeZone" | "planningLeadDays" | "planningStartDaySource"
>;

/**
 * Where a run's `planningStartDay` came from, in words: the rule that made it, or the operator who
 * named it. Display only: the SPA never recomputes a floor (the server records the rule on the run).
 * - default: `anchor America/Chicago, lead 1 day` — each site's first plannable day is the cutoff's
 *   date in its zone (the anchor for a site with none) plus the lead; the earliest is the run's day;
 * - operator: `named by the operator` — the anchor was not consulted;
 * - a run created before the rule was recorded: `rule not recorded`.
 */
export function planningRuleText(run: PlanningRule): string {
  if (run.planningStartDaySource === "operator") return "named by the operator";
  const zone = run.planningAnchorTimeZone;
  const lead = run.planningLeadDays;
  if (!zone && (lead === null || lead === undefined)) return "rule not recorded";
  const parts: string[] = [];
  if (zone) parts.push(`anchor ${zone}`);
  if (lead !== null && lead !== undefined) parts.push(`lead ${formatDays(lead)}`);
  return parts.join(", ");
}

/** `Plans from 16 Sep 2026`: the run's first day, a civil date with no zone. */
export function plansFromText(run: Pick<PlanningRun, "planningStartDay">): string {
  return `Plans from ${formatDate(run.planningStartDay)}`;
}

/**
 * The rule behind a run's first day as a sentence, for the run's detail page. The SPA describes the
 * rule the server recorded; it never recomputes a floor from it.
 */
export function planningRuleSentence(run: PlanningRule): string {
  const zone = run.planningAnchorTimeZone;
  const lead = run.planningLeadDays;
  const recorded = [
    zone ? `anchor ${zone}` : null,
    lead != null ? `lead ${formatDays(lead)}` : null,
  ]
    .filter((part) => part !== null)
    .join(", ");
  if (run.planningStartDaySource === "operator") {
    return (
      "Named by the operator: every site is planned from this day, and no time zone was consulted." +
      (recorded ? ` The configured rule (${recorded}) would otherwise have applied.` : "")
    );
  }
  if (run.planningStartDaySource === "default") {
    const where = zone
      ? `anchor ${zone}, for a site with none`
      : "the anchor zone, for a site with none";
    const plus = lead != null ? formatDays(lead) : "the lead";
    return (
      "Computed from the cutoff: the earliest site's first plannable day, which is the cutoff's date " +
      `in the site's time zone (${where}) plus ${plus}.`
    );
  }
  return recorded
    ? `The rule that produced this day was not recorded for this run (${recorded}).`
    : "The rule that produced this day was not recorded for this run.";
}

/** The zone this browser reads instants in (the viewer's), or a plain phrase when it cannot say. */
export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "your browser's time zone";
  } catch {
    return "your browser's time zone";
  }
}

/**
 * What the New-run form's planning-start-day field means, in the words of the contract: the zone
 * context a planner needs when they do (or do not) name a day. `rule` is the selected scope's
 * anchor zone and lead (`useScopePlanningRule`), null where the SPA cannot read them.
 * `defaultZone` is the zone a site with none is in: the tenant's default site zone
 * (`useDefaultSiteZone`, PS-D9), which differs from the anchor for a non-default scope. Omitted, it
 * is the anchor (a single-scope tenant, where the two are one zone).
 */
export function planningStartNote(
  day: string,
  rule: { zone: string | null; leadDays: number | null; defaultZone?: string | null },
): string {
  if (day) {
    const consulted = rule.zone
      ? ` The default rule (anchor ${rule.zone}${rule.leadDays != null ? `, lead ${formatDays(rule.leadDays)}` : ""}) is not consulted.`
      : "";
    return (
      `Every site is planned from ${formatDate(day)}. It is a calendar date, not an instant: ` +
      `no time zone is applied to it.${consulted} Demand shipping earlier is left out of the solve.`
    );
  }
  const siteZone = rule.defaultZone === undefined ? rule.zone : rule.defaultZone;
  const zone = siteZone
    ? `${siteZone} for a site with none`
    : "the anchor zone for a site with none";
  const lead = rule.leadDays != null ? formatDays(rule.leadDays) : "the configured lead";
  return (
    "Left blank, each site plans from the cutoff's date in its own time zone " +
    `(${zone}) plus ${lead}; the earliest is the run's first day.`
  );
}

/** An integer with thousands separators: `12,345`. */
export function formatCount(count: number): string {
  return count.toLocaleString("en-US");
}

/** One origin's refusals, as the API carries them (`PlanningRun.excludedRowsByOrigin`). */
export type OriginExclusion = components["schemas"]["OriginExclusion"];

/** The noun for a refused input kind, from the tenant's own dictionary (`useLabels`). */
export function inputKindLabel(
  kind: OriginExclusion["inputKind"],
  labels: Pick<Labels, "transferRequests" | "bookedLoads" | "reservedCapacity">,
): string {
  if (kind === "booked_load") return labels.bookedLoads;
  if (kind === "reserved_capacity") return labels.reservedCapacity;
  return labels.transferRequests;
}

/**
 * Rows refused across every origin, or `null` when the run has not reported (`null` is not
 * "none": `[]` is). A sum across kinds, for a register's one-figure cell; the detail names each.
 */
export function refusedRowTotal(
  rows: readonly OriginExclusion[] | null | undefined,
): number | null {
  if (rows === null || rows === undefined) return null;
  return rows.reduce((sum, row) => sum + row.excludedRowCount, 0);
}

/**
 * Each origin's refusal as one line, for a tooltip: `Booked loads · 2023 — 14 rows before 17 Sep
 * 2026`. `siteName` resolves the origin's id to its code (the cached site list); an id the list does
 * not hold yet is left out rather than shown as a uuid.
 */
export function describeRefusals(
  rows: readonly OriginExclusion[] | null | undefined,
  siteName: (siteId: string) => string | undefined,
  kindLabel: (kind: OriginExclusion["inputKind"]) => string,
): string[] {
  return (rows ?? []).map((row) => {
    const site = siteName(row.siteId);
    const rowsText = `${formatCount(row.excludedRowCount)} ${row.excludedRowCount === 1 ? "row" : "rows"}`;
    return `${kindLabel(row.inputKind)}${site ? ` · ${site}` : ""} — ${rowsText} before ${formatDate(row.firstPlannableDay)}`;
  });
}

/**
 * One origin-destination pair's closed-day refusals, as the API carries them
 * (`PlanningRun.refusedForClosure`; design-docs/arrival-on-closed-days.md §7.4). Distinct from
 * `OriginExclusion`: a row is counted in one or the other, never both.
 */
export type ClosureRefusal = components["schemas"]["ClosureRefusal"];

/**
 * Demand lines refused for closure (AC-D13); reservations remain usable.
 * With a count, `12 demand lines refused`; without, `Demand lines refused`.
 */
export function closureRefusalText(kind: ClosureRefusal["inputKind"], count?: number): string {
  const [one, many, verb] = ["demand line", "demand lines", "refused"];
  if (count === undefined) return `${many.charAt(0).toUpperCase()}${many.slice(1)} ${verb}`;
  return `${formatCount(count)} ${count === 1 ? one : many} ${verb}`;
}

/**
 * Rows refused for a closed day across every lane and kind, or `null` when the run has not
 * reported (`null` is not "none": `[]` is).
 */
export function closureRefusalTotal(
  rows: readonly ClosureRefusal[] | null | undefined,
): number | null {
  if (rows === null || rows === undefined) return null;
  return rows.reduce((sum, row) => sum + row.refusedRowCount, 0);
}

/** How many distinct origin-destination pairs the refusals fall on. */
export function closureRefusalLanes(rows: readonly ClosureRefusal[] | null | undefined): number {
  return new Set((rows ?? []).map((row) => `${row.fromSiteId}:${row.toSiteId}`)).size;
}

/**
 * Each lane's closed-day refusal as one line, for a tooltip: `2023 → 3111 — 12 demand lines
 * refused`. `siteCode` resolves an id to its code (the cached site list); a lane whose ends the list
 * does not hold yet is named by its count alone rather than by a uuid.
 */
export function describeClosureRefusals(
  rows: readonly ClosureRefusal[] | null | undefined,
  siteCode: (siteId: string) => string | undefined,
): string[] {
  return (rows ?? []).map((row) => {
    const from = siteCode(row.fromSiteId);
    const to = siteCode(row.toSiteId);
    const lane = from && to ? `${from} → ${to} — ` : "";
    return `${lane}${closureRefusalText(row.inputKind, row.refusedRowCount)}`;
  });
}

/**
 * A run is named by its start time, never its id: `'run started 16 Sep 2026, 09:14'`, plus
 * ` · <label>` when the run carries one.
 */
export function runDisplayName(run: Pick<PlanningRun, "createdAt" | "label">): string {
  const base = `run started ${formatDateTime(run.createdAt)}`;
  return run.label ? `${base} · ${run.label}` : base;
}
